import React, { useState } from 'react';
import { Share2, Mail, Link, Check, Copy } from 'lucide-react';
import { Button } from '../../../designSystem/Button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../../designSystem/Dialog';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  propertyName: string;
  propertyId: number;
}

export const ShareModal: React.FC<ShareModalProps> = ({
  isOpen,
  onClose,
  propertyName,
  propertyId,
}) => {
  const [copied, setCopied] = useState(false);
  
  // Generate shareable link (adjust URL as needed)
  const shareUrl = `${window.location.origin}/property/${propertyId}`;
  
  const handleEmailShare = () => {
    const subject = `Property Health Score Report - ${propertyName}`;
    const body = `Hi,\n\nI wanted to share this property health score report with you:\n\n${shareUrl}\n\nThis report includes performance metrics, market positioning, and recommendations for ${propertyName}.\n\nBest regards`;
    
    const mailtoLink = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoLink);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="w-5 h-5" />
            Share Report
          </DialogTitle>
          <DialogDescription>
            Share this property health score report with others
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Email Option */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <Mail className="w-5 h-5 text-blue-600" />
              <span className="font-medium">Email Report</span>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              Open your email client with a pre-filled message and report link
            </p>
            <Button 
              onClick={handleEmailShare}
              className="w-full"
              variant="outline"
            >
              Compose Email
            </Button>
          </div>

          {/* Copy Link Option */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <Link className="w-5 h-5 text-green-600" />
              <span className="font-medium">Copy Link</span>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              Copy shareable link to clipboard
            </p>
            <Button 
              onClick={handleCopyLink}
              className="w-full"
              variant="outline"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Link
                </>
              )}
            </Button>
          </div>
        </div>

        <div className="mt-6 pt-4 border-t">
          <p className="text-xs text-gray-500 text-center">
            Recipients will be able to view the latest version of this report
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};